import {
  CascadedShadowGenerator,
  Mesh,
  Scene,
  StandardMaterial,
  Texture,
  TransformNode,
  Vector3,
} from '@babylonjs/core';
import { RAPIER, type PhysicsWorld } from '@roadworld/physics';
import type { ChunkPayload } from '@roadworld/shared';
import {
  buildBridgeGuardrailMeshes,
  buildBridgePillarMeshes,
  buildBuildingMeshes,
  buildCrossingMeshes,
  buildRoadDecorMeshes,
  buildRoadMeshes,
  buildTerrain,
  buildVegetation,
  buildWaterGeometry,
  carveTerrainForWater,
  rasterizeCover,
  sampleHeightfield,
  type HeightfieldData,
  type MeshGeometry,
} from '@roadworld/world';
import { buildRoadGraph, type RoadGraph } from '../traffic/roadGraph.js';
import { buildBollardBatch, type BollardBatch } from './bollards.js';
import { chunkCenterWorld, type ChunkCoord } from './coords.js';
import {
  makeBuildingRoofMat,
  makeBuildingWallMat,
  makeStandardMat,
  makeTerrainMat,
  uploadGeometry,
} from './meshes.js';
import {
  buildRoadSegmentIndex,
  classifyRoadDetailAt,
  classifySurfaceAt,
  type RoadDetail,
  type RoadSegmentIndex,
  type SurfaceKind,
} from './surface.js';
import { buildTreeBatch, buildTreeBillboardBatch, type TreeBatch } from './trees.js';
import { getWaterMaterial } from './WaterMaterialCache.js';

/** Resolution of the per-chunk landcover RGBA texture. */
const COVER_TEXTURE_RES = 64;

export type ChunkStatus = 'pending' | 'active' | 'unloaded';
export type ChunkLodLevel = 'high' | 'low';

export interface ChunkBuildOptions {
  terrainResolution: number;
  /** Vertices-per-side for the low-LOD terrain. ~half the high-res value gives a 75%
   *  triangle drop while still tracking large hill shapes. */
  lowLodTerrainResolution?: number;
  /** Centerline subdivision (m) for low-LOD roads. ~3× the high-LOD value yields roughly
   *  60% fewer triangles. */
  lowLodRoadSubdivideMeters?: number;
}

const DEFAULT_LOW_LOD_TERRAIN_RES = 64;
const DEFAULT_LOW_LOD_ROAD_SUBDIVIDE = 16;
const DEFAULT_HIGH_LOD_ROAD_SUBDIVIDE = 6;

export class Chunk {
  readonly coord: ChunkCoord;
  readonly chunkSize: number;
  status: ChunkStatus = 'pending';
  private scene: Scene;
  private physics: PhysicsWorld;
  private shadowGenerator: CascadedShadowGenerator | null;
  private root: TransformNode;
  /** Visuals that render at full detail in the active ring. setEnabled(false) when LOD is on
   *  and the chunk is in the cached ring. */
  private highMeshes: Mesh[] = [];
  /** Visuals that render in the cached ring (when LOD is enabled). Replaced when LOD is off
   *  or the chunk is active — see setLOD. */
  private lowMeshes: Mesh[] = [];
  /** Building meshes (walls + roofs) use scene-shared materials/textures, so they must NOT
   *  go through the highMeshes/lowMeshes disposal path (which disposes mesh materials AND
   *  textures via dispose(false, true)). Kept separately and disposed with (false, false)
   *  so the cached building materials survive chunk unload. Still LOD-toggled in setLOD. */
  private buildingMeshesHigh: Mesh[] = [];
  private buildingMeshesLow: Mesh[] = [];
  /** Currently visible LOD. setLOD short-circuits when this matches. */
  private currentLod: ChunkLodLevel = 'high';
  /** Meshes registered with the shadow generator — must be deregistered on dispose. */
  private shadowCasters: Mesh[] = [];
  /** Per-chunk tree batches. The high variant uses trunk+crown cylinders; the low variant
   *  uses a single billboard quad per tree. Both share the same TreeInstance layout so
   *  hide()-based fell semantics apply to both via the wrapping TreeBatch returned by
   *  getTreeBatch(). */
  private treeBatchHigh: TreeBatch | null = null;
  private treeBatchLow: TreeBatch | null = null;
  /** Cached wrapper exposed via getTreeBatch — proxies hide/isHidden to whichever batches
   *  exist. Built once after vegetation generation. */
  private treeBatchProxy: TreeBatch | null = null;
  /** Water polygons. Kept in a dedicated array (not the high/low LOD buckets) because
   *  water should remain visible in *both* the active and cached rings — a lake or river
   *  cutting off at the active-ring boundary would look broken. Water geometry is light
   *  enough that no LOD variant is worth maintaining. */
  private waterMeshes: Mesh[] = [];
  /** Road-decoration meshes (markings, shoulders, fillets, parking fill). High-LOD only —
   *  toggled with the high ring in setLOD. Kept separate from highMeshes because they share
   *  scene-wide frozen materials, so they must dispose WITHOUT freeing their material. */
  private decorMeshes: Mesh[] = [];
  /** Per-chunk bollard batch (thin-instanced cylinders along highway shoulders). Built at
   *  high LOD only (fine detail not worth rendering in the cached ring). */
  private bollardBatch: BollardBatch | null = null;
  private heightfield: HeightfieldData | null = null;
  private collider: RAPIER.Collider | null = null;
  private body: RAPIER.RigidBody | null = null;
  private buildingBody: RAPIER.RigidBody | null = null;
  /** Per-chunk landcover texture — disposed on unload. */
  private coverTexture: Texture | null = null;
  /** CPU-side copy of the landcover bytes so we can sample per-position at runtime (e.g. for particle tinting). */
  private coverData: Uint8Array | null = null;
  private coverResolution = 0;
  /** Trimesh collider for bridge ramps + decks. null if the chunk has no bridges. */
  private bridgeBody: RAPIER.RigidBody | null = null;
  /** Spatial index over road/path segments for surface-type queries. */
  private roadIndex: RoadSegmentIndex | null = null;
  /** Directed road graph for traffic pathfinding. Built alongside the road index. */
  private roadGraph: RoadGraph | null = null;
  /** Cached payload — kept so the traffic system can re-walk the OSM ways at spawn time
   *  without re-fetching. Cleared on dispose. */
  private payload: ChunkPayload | null = null;

  constructor(
    scene: Scene,
    physics: PhysicsWorld,
    coord: ChunkCoord,
    chunkSize: number,
    shadowGenerator: CascadedShadowGenerator | null = null,
  ) {
    this.scene = scene;
    this.physics = physics;
    this.shadowGenerator = shadowGenerator;
    this.coord = coord;
    this.chunkSize = chunkSize;
    const center = chunkCenterWorld(coord, chunkSize);
    this.root = new TransformNode(`chunk-${coord.cx},${coord.cz}`, scene);
    this.root.position.set(center.x, 0, center.z);
  }

  /** Build meshes and physics from the fetched payload. Marks the chunk active.
   *
   * Builds TWO visual variants — high-LOD for active-ring rendering and low-LOD for
   * cached-ring rendering — sharing the high-res heightfield and physics colliders.
   * setLOD(level) flips visibility between them with zero rebuild cost.
   */
  build(payload: ChunkPayload, opts: ChunkBuildOptions): void {
    if (this.status === 'unloaded') return;
    this.payload = payload;

    const lowTerrainRes = opts.lowLodTerrainResolution ?? DEFAULT_LOW_LOD_TERRAIN_RES;
    const lowRoadSubdivide = opts.lowLodRoadSubdivideMeters ?? DEFAULT_LOW_LOD_ROAD_SUBDIVIDE;

    // Shared (LOD-independent) data: heightfield used for physics + ground-sampling, road
    // index for surface classification, road graph for traffic. Built at high resolution
    // even when the chunk renders at low LOD — colliders are invisible regardless.
    const highTerrain = buildTerrain(payload, { resolution: opts.terrainResolution });
    this.heightfield = highTerrain.heightfield;
    // Drop the visual terrain mesh below water polygons so SRTM-noisy interpolation
    // doesn't poke up through the flat water plane. The heightfield is left at raw SRTM
    // for physics + road/bridge construction.
    carveTerrainForWater(
      payload,
      highTerrain.heightfield,
      highTerrain.mesh.positions,
      opts.terrainResolution,
    );
    this.roadIndex = buildRoadSegmentIndex(payload);
    this.roadGraph = buildRoadGraph(payload, this.coord, chunkCenterWorld(this.coord, this.chunkSize));

    // -- HIGH-LOD VISUALS --
    const highRoads = buildRoadMeshes(payload, highTerrain.heightfield, {
      liftDrivable: 0.1,
      liftPath: 0.05,
      subdivideMeters: DEFAULT_HIGH_LOD_ROAD_SUBDIVIDE,
    });

    const highTerrainMesh = uploadGeometry(`terrain-hi-${this.key()}`, highTerrain.mesh, this.scene);
    highTerrainMesh.parent = this.root;
    // Use the landcover-aware terrain material (rasterized from OSM landuse polygons).
    // Falls back to a flat palette green when terrain shading is diagnosed off.
    const cover = rasterizeCover(payload, COVER_TEXTURE_RES);
    const terrainMat = makeTerrainMat(`terrain-mat-${this.key()}`, this.scene, cover);
    highTerrainMesh.material = terrainMat.material;
    this.coverTexture = terrainMat.texture;
    this.coverData = cover.rgba;
    this.coverResolution = cover.resolution;
    highTerrainMesh.receiveShadows = true;
    this.highMeshes.push(highTerrainMesh);

    if (highRoads.drivable.indices.length > 0) {
      const m = uploadGeometry(`roads-hi-${this.key()}`, highRoads.drivable, this.scene);
      m.parent = this.root;
      m.material = makeStandardMat(`roads-mat-${this.key()}`, this.scene, [0.16, 0.16, 0.18], -2);
      m.receiveShadows = true;
      this.highMeshes.push(m);
    }
    if (highRoads.paths.indices.length > 0) {
      const m = uploadGeometry(`paths-hi-${this.key()}`, highRoads.paths, this.scene);
      m.parent = this.root;
      m.material = makeStandardMat(`paths-mat-${this.key()}`, this.scene, [0.55, 0.5, 0.42], -1);
      this.highMeshes.push(m);
    }

    // Road decoration: shoulder strips, intersection fillets, lane markings, bollards,
    // and parking-lot fill. All driven by the road's OSM class (and lanes tag where
    // present); see `roadStyle()` for the per-class rules. High-LOD only — fine detail
    // isn't worth rendering in the cached ring, so these live in decorMeshes and are
    // hidden together with the high ring when the chunk drops to low LOD.
    //
    // Perf: materials are shared scene-wide (one per decor type, not one per chunk) and
    // frozen, and each mesh is frozen + non-pickable. With a dense urban ring this turns
    // ~54 per-chunk decor materials into 6 shared ones and removes per-frame world-matrix
    // and picking work from hundreds of static decals.
    const decor = buildRoadDecorMeshes(payload, highTerrain.heightfield);
    const dm = getSharedDecorMaterials(this.scene);
    const decorHighOn = this.currentLod === 'high';
    const addDecor = (name: string, geom: MeshGeometry, mat: StandardMaterial): void => {
      const m = uploadGeometry(name, geom, this.scene);
      m.parent = this.root;
      m.material = mat;
      m.isPickable = false;
      m.freezeWorldMatrix();
      m.setEnabled(decorHighOn);
      this.decorMeshes.push(m);
    };

    if (decor.parkingFill.indices.length > 0)
      addDecor(`parking-fill-${this.key()}`, decor.parkingFill, dm.parkingFill);
    if (decor.shoulderGrass.indices.length > 0)
      addDecor(`shoulder-grass-${this.key()}`, decor.shoulderGrass, dm.shoulderGrass);
    if (decor.shoulderPavement.indices.length > 0)
      addDecor(`shoulder-pavement-${this.key()}`, decor.shoulderPavement, dm.shoulderPavement);
    if (decor.intersectionFillets.indices.length > 0)
      addDecor(`fillets-${this.key()}`, decor.intersectionFillets, dm.fillets);
    if (decor.markingsWhite.indices.length > 0)
      addDecor(`markings-w-${this.key()}`, decor.markingsWhite, dm.markingsWhite);
    if (decor.markingsYellow.indices.length > 0)
      addDecor(`markings-y-${this.key()}`, decor.markingsYellow, dm.markingsYellow);
    if (decor.bollards.length > 0) {
      const offset = chunkCenterWorld(this.coord, this.chunkSize);
      this.bollardBatch = buildBollardBatch(
        this.scene,
        this.key(),
        decor.bollards,
        new Vector3(offset.x, 0, offset.z),
      );
      this.bollardBatch?.setEnabled(decorHighOn);
    }

    // Bridge trimesh collider — sourced from high-LOD bridge geometry. Physics is shared
    // across LODs (the player can't drive on a cached-ring bridge anyway).
    if (highRoads.bridges.indices.length > 0) {
      const center = chunkCenterWorld(this.coord, this.chunkSize);
      const bodyDesc = RAPIER.RigidBodyDesc.fixed().setTranslation(center.x, 0, center.z);
      const bridgeBody = this.physics.world.createRigidBody(bodyDesc);
      const colliderDesc = RAPIER.ColliderDesc.trimesh(
        highRoads.bridges.positions,
        highRoads.bridges.indices,
      ).setFriction(0.95);
      this.physics.world.createCollider(colliderDesc, bridgeBody);
      this.bridgeBody = bridgeBody;
    }

    // Bridge abutment pillars and guardrails — high-LOD only. Dropping them for cached
    // chunks costs nothing visually at distance and saves geometry on bridge-heavy chunks.
    const pillars = buildBridgePillarMeshes(payload, highTerrain.heightfield);
    if (pillars.indices.length > 0) {
      const m = uploadGeometry(`pillars-hi-${this.key()}`, pillars, this.scene);
      m.parent = this.root;
      m.material = makeStandardMat(`pillars-mat-${this.key()}`, this.scene, [0.62, 0.6, 0.58]);
      this.highMeshes.push(m);
    }

    const guardrails = buildBridgeGuardrailMeshes(payload, highTerrain.heightfield);
    if (guardrails.indices.length > 0) {
      const m = uploadGeometry(`guardrails-hi-${this.key()}`, guardrails, this.scene);
      m.parent = this.root;
      m.material = makeStandardMat(`guardrails-mat-${this.key()}`, this.scene, [0.78, 0.78, 0.78]);
      this.highMeshes.push(m);
    }

    // Zebra crossings — high-LOD only (small markings, invisible past the active ring).
    const crossings = buildCrossingMeshes(payload, highTerrain.heightfield);
    if (crossings.indices.length > 0) {
      const m = uploadGeometry(`crossings-hi-${this.key()}`, crossings, this.scene);
      m.parent = this.root;
      m.material = makeStandardMat(`crossings-mat-${this.key()}`, this.scene, [0.95, 0.95, 0.95], -3);
      this.highMeshes.push(m);
    }

    // Buildings: high-LOD visual (with skirts and inner-ring holes) + collider sourced
    // from the same geometry so collision matches what you see in the active ring.
    if (payload.osm.buildings.length > 0) {
      const highBuildings = buildBuildingMeshes(payload, highTerrain.heightfield, { lodLevel: 'high' });
      const center = chunkCenterWorld(this.coord, this.chunkSize);
      let buildingBody: RAPIER.RigidBody | null = null;
      const ensureBody = (): RAPIER.RigidBody => {
        if (!buildingBody) {
          const bodyDesc = RAPIER.RigidBodyDesc.fixed().setTranslation(center.x, 0, center.z);
          buildingBody = this.physics.world.createRigidBody(bodyDesc);
        }
        return buildingBody;
      };

      if (highBuildings.walls.indices.length > 0) {
        const m = uploadGeometry(`buildings-walls-hi-${this.key()}`, highBuildings.walls, this.scene);
        m.parent = this.root;
        m.material = makeBuildingWallMat(this.scene);
        m.receiveShadows = true;
        this.buildingMeshesHigh.push(m);
        this.addShadowCaster(m, false);

        const colliderDesc = RAPIER.ColliderDesc.trimesh(
          highBuildings.walls.positions,
          highBuildings.walls.indices,
        ).setFriction(0.6);
        this.physics.world.createCollider(colliderDesc, ensureBody());
      }

      if (highBuildings.roofs.indices.length > 0) {
        const m = uploadGeometry(`buildings-roofs-hi-${this.key()}`, highBuildings.roofs, this.scene);
        m.parent = this.root;
        m.material = makeBuildingRoofMat(this.scene);
        m.receiveShadows = true;
        this.buildingMeshesHigh.push(m);
        this.addShadowCaster(m, false);

        const colliderDesc = RAPIER.ColliderDesc.trimesh(
          highBuildings.roofs.positions,
          highBuildings.roofs.indices,
        ).setFriction(0.6);
        this.physics.world.createCollider(colliderDesc, ensureBody());
      }

      this.buildingBody = buildingBody;
    }

    // Water — OSM polygons (lakes, river polys, buffered rivers). Visual only, no
    // collider. LOD-independent (stored in `waterMeshes`, not high/low) so distant water
    // doesn't vanish at the active-ring boundary.
    if (payload.osm.water.length > 0) {
      const waterMeshes = buildWaterGeometry(payload, highTerrain.heightfield);
      if (waterMeshes.length > 0) {
        const waterMat = getWaterMaterial(this.scene);
        for (const w of waterMeshes) {
          const m = uploadGeometry(`water-${this.key()}-${w.id}`, w.geometry, this.scene);
          m.parent = this.root;
          m.material = waterMat;
          // Water shouldn't pick up shadow maps and shouldn't cast.
          m.receiveShadows = false;
          this.waterMeshes.push(m);
        }
      }
    }

    // Vegetation — placement is LOD-independent; both batches render the same trees with
    // different geometry. Felling propagates to both via the proxy returned by getTreeBatch.
    const vegetation = buildVegetation(payload, highTerrain.heightfield);
    if (vegetation.trees.length > 0) {
      const offset = chunkCenterWorld(this.coord, this.chunkSize);
      const root = new Vector3(offset.x, 0, offset.z);
      this.treeBatchHigh = buildTreeBatch(this.scene, this.key(), vegetation.trees, root);
      this.treeBatchLow = buildTreeBillboardBatch(this.scene, this.key(), vegetation.trees, root);
      this.treeBatchProxy = makeTreeBatchProxy(this.treeBatchHigh, this.treeBatchLow);
      // Visual-overhaul: register the high-LOD trees as shadow casters (thin-instance
      // base meshes; `true` includes instances). Low-LOD billboards are skipped — flat
      // quad silhouettes look wrong projected on the ground.
      if (this.treeBatchHigh) {
        for (const m of this.treeBatchHigh.baseMeshes) this.addShadowCaster(m, true);
      }
    }

    // -- LOW-LOD VISUALS --
    // Reduced-resolution terrain mesh (heightfield/physics already built from the high one).
    const lowTerrain = buildTerrain(payload, { resolution: lowTerrainRes });
    const lowTerrainMesh = uploadGeometry(`terrain-lo-${this.key()}`, lowTerrain.mesh, this.scene);
    lowTerrainMesh.parent = this.root;
    lowTerrainMesh.material = makeStandardMat(`terrain-mat-${this.key()}`, this.scene, [0.32, 0.45, 0.22]);
    lowTerrainMesh.receiveShadows = true;
    this.lowMeshes.push(lowTerrainMesh);

    // Roads — same geometry kind, just coarser centerline subdivision.
    const lowRoads = buildRoadMeshes(payload, highTerrain.heightfield, {
      liftDrivable: 0.1,
      liftPath: 0.05,
      subdivideMeters: lowRoadSubdivide,
    });
    if (lowRoads.drivable.indices.length > 0) {
      const m = uploadGeometry(`roads-lo-${this.key()}`, lowRoads.drivable, this.scene);
      m.parent = this.root;
      m.material = makeStandardMat(`roads-mat-${this.key()}`, this.scene, [0.16, 0.16, 0.18], -2);
      this.lowMeshes.push(m);
    }
    if (lowRoads.paths.indices.length > 0) {
      const m = uploadGeometry(`paths-lo-${this.key()}`, lowRoads.paths, this.scene);
      m.parent = this.root;
      m.material = makeStandardMat(`paths-mat-${this.key()}`, this.scene, [0.55, 0.5, 0.42], -1);
      this.lowMeshes.push(m);
    }

    // Buildings — outer-ring-only extrusions with no skirt.
    if (payload.osm.buildings.length > 0) {
      const lowBuildings = buildBuildingMeshes(payload, highTerrain.heightfield, { lodLevel: 'low' });
      if (lowBuildings.walls.indices.length > 0) {
        const m = uploadGeometry(`buildings-walls-lo-${this.key()}`, lowBuildings.walls, this.scene);
        m.parent = this.root;
        // Low LOD skips the window facade — just the per-building wall tint via vertex colors.
        m.material = makeBuildingRoofMat(this.scene);
        this.buildingMeshesLow.push(m);
      }
      if (lowBuildings.roofs.indices.length > 0) {
        const m = uploadGeometry(`buildings-roofs-lo-${this.key()}`, lowBuildings.roofs, this.scene);
        m.parent = this.root;
        m.material = makeBuildingRoofMat(this.scene);
        this.buildingMeshesLow.push(m);
      }
    }

    const center = chunkCenterWorld(this.coord, this.chunkSize);
    const { body, collider } = createHeightfieldCollider(
      this.physics,
      highTerrain.heightfield,
      center.x,
      center.z,
    );
    this.body = body;
    this.collider = collider;

    // Default LOD is high. ChunkManager flips to 'low' on the next streaming pass for any
    // chunk that lands in the cached band.
    this.setLOD('high');
    this.status = 'active';
  }

  private addShadowCaster(mesh: Mesh, includeChildren: boolean): void {
    if (!this.shadowGenerator) return;
    this.shadowGenerator.addShadowCaster(mesh, includeChildren);
    this.shadowCasters.push(mesh);
  }

  /** Toggle visibility between high and low LOD variants. Cheap — every per-LOD mesh is
   *  setEnabled-flipped in place. The tree batches' base meshes are setEnabled-flipped via
   *  the underlying mesh; thin-instance buffers are untouched. */
  setLOD(level: ChunkLodLevel): void {
    if (this.currentLod === level && this.status === 'active') return;
    this.currentLod = level;
    const highOn = level === 'high';
    for (const m of this.highMeshes) m.setEnabled(highOn);
    for (const m of this.lowMeshes) m.setEnabled(!highOn);
    for (const m of this.buildingMeshesHigh) m.setEnabled(highOn);
    for (const m of this.buildingMeshesLow) m.setEnabled(!highOn);
    // Tree batches own their base mesh; flip enabled on that mesh directly via the
    // batch's dispose/hide doesn't help us here — instead we cast to access the mesh.
    setTreeBatchEnabled(this.treeBatchHigh, highOn);
    setTreeBatchEnabled(this.treeBatchLow, !highOn);
    // Road decor + bollards are high-LOD-only detail — hide them in the cached ring.
    for (const m of this.decorMeshes) m.setEnabled(highOn);
    this.bollardBatch?.setEnabled(highOn);
  }

  getLOD(): ChunkLodLevel {
    return this.currentLod;
  }

  /** Sample heightfield in WORLD coords. Returns null if (x, z) is outside this chunk. */
  sampleWorldHeight(worldX: number, worldZ: number): number | null {
    if (!this.heightfield) return null;
    const center = chunkCenterWorld(this.coord, this.chunkSize);
    const localX = worldX - center.x;
    const localZ = worldZ - center.z;
    const half = this.chunkSize * 0.5;
    if (localX < -half || localX > half || localZ < -half || localZ > half) return null;
    return sampleHeightfield(this.heightfield, localX, localZ);
  }

  /**
   * Sample the landcover color (0..1 RGB) at the given world position. Used by particle
   * tinting so off-road dust matches the terrain underneath. Returns null if outside this chunk.
   */
  sampleCoverColor(worldX: number, worldZ: number): [number, number, number] | null {
    if (!this.coverData || this.coverResolution === 0) return null;
    const center = chunkCenterWorld(this.coord, this.chunkSize);
    const localX = worldX - center.x;
    const localZ = worldZ - center.z;
    const half = this.chunkSize * 0.5;
    if (localX < -half || localX > half || localZ < -half || localZ > half) return null;
    const u = (localX + half) / this.chunkSize;
    const v = (localZ + half) / this.chunkSize;
    const px = Math.min(this.coverResolution - 1, Math.max(0, Math.floor(u * this.coverResolution)));
    const py = Math.min(this.coverResolution - 1, Math.max(0, Math.floor(v * this.coverResolution)));
    const idx = (py * this.coverResolution + px) * 4;
    return [
      (this.coverData[idx] ?? 0) / 255,
      (this.coverData[idx + 1] ?? 0) / 255,
      (this.coverData[idx + 2] ?? 0) / 255,
    ];
  }

  /** Classify the surface at WORLD coords. Returns null if (x, z) is outside this chunk.
   *  `margin` (m) optionally widens the road/path match — see classifySurfaceAt. */
  classifyWorldSurface(worldX: number, worldZ: number, margin = 0): SurfaceKind | null {
    if (!this.roadIndex) return null;
    const center = chunkCenterWorld(this.coord, this.chunkSize);
    const localX = worldX - center.x;
    const localZ = worldZ - center.z;
    const half = this.chunkSize * 0.5;
    if (localX < -half || localX > half || localZ < -half || localZ > half) return null;
    return classifySurfaceAt(this.roadIndex, localX, localZ, margin);
  }

  /**
   * Like classifyWorldSurface, but returns the OSM metadata of the matching road segment
   * (class, lanes, oneway, etc.). Returns null when off-road, or when the chunk isn't
   * built yet, or when (x, z) is outside this chunk. Used by the road-class debug HUD.
   */
  classifyWorldRoadDetail(worldX: number, worldZ: number): RoadDetail | null {
    if (!this.roadIndex) return null;
    const center = chunkCenterWorld(this.coord, this.chunkSize);
    const localX = worldX - center.x;
    const localZ = worldZ - center.z;
    const half = this.chunkSize * 0.5;
    if (localX < -half || localX > half || localZ < -half || localZ > half) return null;
    return classifyRoadDetailAt(this.roadIndex, localX, localZ);
  }

  isActive(): boolean {
    return this.status === 'active';
  }

  key(): string {
    return `${this.coord.cx},${this.coord.cz}`;
  }

  /** Read-only access to the chunk's tree batch (for the impact system). Returns a proxy
   *  that fans hide()/isHidden() out to both the cylinder and billboard variants so a
   *  felled tree disappears in both LOD levels. */
  getTreeBatch(): TreeBatch | null {
    return this.treeBatchProxy;
  }

  /** Refresh billboard-tree facing rotation for this chunk. No-op if the chunk has no
   *  trees or isn't at low LOD. Called per-frame from the render loop with the chase
   *  camera's world XZ. */
  updateBillboardTrees(cameraX: number, cameraZ: number): void {
    this.treeBatchLow?.updateBillboard(cameraX, cameraZ);
  }

  /** Read-only access to the raw OSM payload — used by the traffic system to walk drivable
   *  ways at spawn time. Null after dispose. */
  getPayload(): ChunkPayload | null {
    return this.payload;
  }

  /** Directed road graph for traffic pathfinding. Null after dispose. */
  getRoadGraph(): RoadGraph | null {
    return this.roadGraph;
  }

  dispose(): void {
    if (this.status === 'unloaded') return;
    if (this.shadowGenerator) {
      for (const m of this.shadowCasters) this.shadowGenerator.removeShadowCaster(m);
    }
    this.shadowCasters = [];
    for (const m of this.highMeshes) m.dispose(false, true);
    for (const m of this.lowMeshes) m.dispose(false, true);
    for (const m of this.waterMeshes) m.dispose(false, true);
    // Decor meshes share scene-wide materials — dispose geometry only, never the material.
    for (const m of this.decorMeshes) m.dispose(false, false);
    // Building meshes share scene-cached materials/textures — dispose geometry only.
    for (const m of this.buildingMeshesHigh) m.dispose(false, false);
    for (const m of this.buildingMeshesLow) m.dispose(false, false);
    this.highMeshes = [];
    this.lowMeshes = [];
    this.waterMeshes = [];
    this.decorMeshes = [];
    this.buildingMeshesHigh = [];
    this.buildingMeshesLow = [];
    if (this.treeBatchHigh) this.treeBatchHigh.dispose();
    if (this.treeBatchLow) this.treeBatchLow.dispose();
    this.treeBatchHigh = null;
    this.treeBatchLow = null;
    this.treeBatchProxy = null;
    if (this.bollardBatch) this.bollardBatch.dispose();
    this.bollardBatch = null;
    if (this.coverTexture) this.coverTexture.dispose();
    this.coverTexture = null;
    this.coverData = null;
    this.coverResolution = 0;
    if (this.body) this.physics.world.removeRigidBody(this.body);
    if (this.buildingBody) this.physics.world.removeRigidBody(this.buildingBody);
    if (this.bridgeBody) this.physics.world.removeRigidBody(this.bridgeBody);
    this.body = null;
    this.buildingBody = null;
    this.bridgeBody = null;
    this.collider = null;
    this.heightfield = null;
    this.roadIndex = null;
    this.roadGraph = null;
    this.payload = null;
    this.root.dispose();
    this.status = 'unloaded';
  }
}

function setTreeBatchEnabled(batch: TreeBatch | null, on: boolean): void {
  if (batch) batch.setEnabled(on);
}

interface SharedDecorMaterials {
  parkingFill: StandardMaterial;
  shoulderGrass: StandardMaterial;
  shoulderPavement: StandardMaterial;
  fillets: StandardMaterial;
  markingsWhite: StandardMaterial;
  markingsYellow: StandardMaterial;
}

/** Road-decor materials are identical across every chunk, so we build one set per scene and
 *  reuse it. This collapses ~6 materials-per-chunk (≈54 across a dense active ring) down to 6
 *  total, cutting material memory and GPU state changes. Frozen because they never change.
 *  Lives for the scene lifetime — never disposed, so chunk teardown must dispose decor meshes
 *  WITHOUT freeing their material (see dispose()). */
const decorMaterialCache = new WeakMap<Scene, SharedDecorMaterials>();

function getSharedDecorMaterials(scene: Scene): SharedDecorMaterials {
  const cached = decorMaterialCache.get(scene);
  if (cached) return cached;
  const mk = (name: string, rgb: [number, number, number], z = 0): StandardMaterial => {
    const mat = makeStandardMat(name, scene, rgb, z);
    mat.freeze();
    return mat;
  };
  const mats: SharedDecorMaterials = {
    parkingFill: mk('decor-parking-fill', [0.16, 0.16, 0.18], -1),
    shoulderGrass: mk('decor-shoulder-grass', [0.34, 0.4, 0.22]),
    shoulderPavement: mk('decor-shoulder-pavement', [0.62, 0.62, 0.6]),
    fillets: mk('decor-fillets', [0.16, 0.16, 0.18], -3),
    markingsWhite: mk('decor-markings-w', [0.94, 0.94, 0.9], -5),
    markingsYellow: mk('decor-markings-y', [0.95, 0.8, 0.18], -5),
  };
  decorMaterialCache.set(scene, mats);
  return mats;
}

/**
 * Wraps the high-LOD and low-LOD tree batches in a single TreeBatch facade. The impact
 * system holds a long-lived reference to the batch — by returning a proxy we keep that
 * reference stable across LOD swaps, and a fell propagates to both variants so the tree
 * also disappears from the billboard view if the chunk later goes cached.
 *
 * Trees iterable, worldX/worldZ, and indices are identical across both batches (same
 * source TreeInstance list), so we expose the high batch's arrays for callers that need
 * positional data.
 */
function makeTreeBatchProxy(high: TreeBatch | null, low: TreeBatch | null): TreeBatch {
  // At least one batch must exist for this function to be called.
  const ref = high ?? low!;
  return {
    trees: ref.trees,
    worldX: ref.worldX,
    worldZ: ref.worldZ,
    // Proxy doesn't expose shadow-caster meshes — callers that need to register them with
    // a shadow generator should hold the underlying high batch directly (we do that in
    // build()).
    baseMeshes: [],
    hide(i: number): void {
      high?.hide(i);
      low?.hide(i);
    },
    isHidden(i: number): boolean {
      return high ? high.isHidden(i) : low!.isHidden(i);
    },
    setEnabled(on: boolean): void {
      high?.setEnabled(on);
      low?.setEnabled(on);
    },
    updateBillboard(cameraX: number, cameraZ: number): void {
      // Only the low batch does real work; the high batch's impl is a no-op.
      low?.updateBillboard(cameraX, cameraZ);
    },
    dispose(): void {
      // Disposal is owned by Chunk; the proxy is a value object. No-op here.
    },
  };
}

function createHeightfieldCollider(
  physics: PhysicsWorld,
  h: HeightfieldData,
  worldX: number,
  worldZ: number,
): { body: RAPIER.RigidBody; collider: RAPIER.Collider } {
  const nrows = h.resolution - 1;
  const ncols = h.resolution - 1;
  const resolution = h.resolution;
  const rapierHeights = new Float32Array(resolution * resolution);
  for (let z = 0; z < resolution; z++) {
    for (let x = 0; x < resolution; x++) {
      rapierHeights[x * resolution + z] = h.heights[z * resolution + x] ?? 0;
    }
  }
  const scale = { x: h.sizeMeters, y: 1, z: h.sizeMeters };
  const bodyDesc = RAPIER.RigidBodyDesc.fixed().setTranslation(worldX, 0, worldZ);
  const body = physics.world.createRigidBody(bodyDesc);
  const colliderDesc = RAPIER.ColliderDesc.heightfield(nrows, ncols, rapierHeights, scale).setFriction(
    0.95,
  );
  const collider = physics.world.createCollider(colliderDesc, body);
  return { body, collider };
}
